import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Loader2,
  RotateCcw,
  X,
  Minus
} from 'lucide-react';
import useJobProcessingStore from '../../store/jobProcessingStore';
import useJobStore from '../../store/jobStore';
import { runJobProcessingStream } from '../../services/jobProcessingStream';

const activeStatuses = new Set(['pending', 'processing']);
const cancellableStatuses = new Set(['pending', 'processing', 'failed']);
const notificationEvents = new Set(['run.core_ready', 'run.completed', 'run.failed']);
const successfulNotificationEvents = new Set(['run.core_ready', 'run.completed']);
const refreshEvents = new Set(['run.completed', 'run.failed', 'run.cancelled']);
const backgroundStages = new Set(['building_matching_profile', 'matching_profile_queued']);
const publishReadyStages = new Set(['core_sections_ready', 'applying_sections']);

const stageCopy = {
  queued: 'Queued for AI drafting',
  starting: 'Starting secure processing',
  extracting_text: 'Reading the job description',
  analyzing_document: 'Structuring the role',
  core_role_ready: 'Role details ready',
  core_requirements_ready: 'Requirements ready',
  core_compensation_ready: 'Compensation and timing ready',
  core_sections_ready: 'Ready to publish - enrichment continues in the background',
  applying_sections: 'Ready to publish - finalizing background enrichment',
  building_matching_profile: 'Finalizing matching in the background',
  retry_queued: 'Retry queued',
  ready_for_review: 'Ready for your review',
  failed: 'Needs your attention',
  cancelled: 'Continue manually'
};

const getStageCopy = (run) => stageCopy[run.stage] || 'Preparing your draft';
const isBackgroundRun = (run) => activeStatuses.has(run?.status) && backgroundStages.has(run?.stage);
const isPublishReadyRun = (run) => activeStatuses.has(run?.status) && publishReadyStages.has(run?.stage);
const isActiveRun = (run) => activeStatuses.has(run?.status) && !isBackgroundRun(run) && !isPublishReadyRun(run);

const JobProcessingCoordinator = () => {
  const hydrate = useJobProcessingStore(state => state.hydrate);
  const applyEvent = useJobProcessingStore(state => state.applyEvent);
  const setStreamConnected = useJobProcessingStore(state => state.setStreamConnected);
  const fetchEmployerJobs = useJobStore(state => state.fetchEmployerJobs);
  const notifiedEvents = useRef(new Set());
  const initialAfter = useRef(useJobProcessingStore.getState().lastEventId);

  useEffect(() => {
    hydrate();
    const controller = new AbortController();
    runJobProcessingStream({
      signal: controller.signal,
      after: initialAfter.current,
      onConnectionChange: setStreamConnected,
      onEvent: (event) => {
        applyEvent(event);
        const jobStore = useJobStore.getState();
        if (refreshEvents.has(event.type)) {
          fetchEmployerJobs();
          if (event.processing?.jobId && String(jobStore.currentJob?.id) === String(event.processing.jobId)) {
            jobStore.fetchJobById(event.processing.jobId);
          }
        }

        if (!notificationEvents.has(event.type) || notifiedEvents.current.has(event.id)) return;
        notifiedEvents.current.add(event.id);

        const isFresh = Date.now() - new Date(event.createdAt).getTime() < 60000;
        if (isFresh) {
          const message = event.payload?.message || (event.type === 'run.completed'
            ? 'Your AI job draft is ready for review.'
            : 'Job draft processing needs attention.');
          const successful = successfulNotificationEvents.has(event.type);
          if (successful) toast.success(message, { duration: 7000 });
          else toast.error(message, { duration: 8000 });

          if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
            try {
              new Notification(successful ? 'Job draft ready' : 'Job processing needs attention', {
                body: message,
                tag: `job-processing-${event.runId}`
              });
            } catch (_error) {
              // The in-app notification remains available when browser notifications are restricted.
            }
          }
        }
      }
    });

    const fallbackTimer = setInterval(() => {
      if (document.visibilityState === 'visible') hydrate();
    }, 30000);

    return () => {
      controller.abort();
      clearInterval(fallbackTimer);
    };
  }, [applyEvent, fetchEmployerJobs, hydrate, setStreamConnected]);

  return null;
};

export const JobProcessingTray = () => {
  const navigate = useNavigate();
  const runs = useJobProcessingStore(state => state.runs);
  const dismissedRunIds = useJobProcessingStore(state => state.dismissedRunIds);
  const streamConnected = useJobProcessingStore(state => state.streamConnected);
  const retry = useJobProcessingStore(state => state.retry);
  const dismiss = useJobProcessingStore(state => state.dismiss);
  const cancel = useJobProcessingStore(state => state.cancel);
  const jobs = useJobStore(state => state.jobs);
  const currentJob = useJobStore(state => state.currentJob);
  const [isMinimized, setIsMinimized] = useState(false);

  const visibleRuns = useMemo(() => {
    const knownJobs = new Map(jobs.map(job => [String(job.id), job]));
    if (currentJob?.id) knownJobs.set(String(currentJob.id), currentJob);

    return Object.values(runs)
      .filter(run => {
        const job = knownJobs.get(String(run?.jobId));
        return (
          run?.id &&
          (!job || job.status === 'draft') &&
          !dismissedRunIds[String(run.id)] &&
          !isBackgroundRun(run) &&
          run.status !== 'cancelled' &&
          (run.status !== 'completed' || run.needsReview !== false)
        );
      })
      .sort((left, right) => {
        const leftActive = isActiveRun(left) ? 1 : 0;
        const rightActive = isActiveRun(right) ? 1 : 0;
        if (leftActive !== rightActive) return rightActive - leftActive;
        return new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime();
      })
      .slice(0, 4);
  }, [currentJob, dismissedRunIds, jobs, runs]);

  if (!visibleRuns.length) return null;

  const activeCount = visibleRuns.filter(isActiveRun).length;

  const handleRetry = async (run) => {
    const result = await retry(run.id);
    if (result.success) toast.success('Job processing retry queued.');
    else toast.error(result.error);
  };

  const handleCancel = async (run) => {
    const result = await cancel(run.id);
    if (result.success) {
      toast.success(run.status === 'pending'
        ? 'Queued processing cancelled.'
        : 'AI processing stopped. Continue manually.');
    }
    else toast.error(result.error);
  };

  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#0b1f3a] text-white shadow-[0_12px_40px_rgba(15,23,42,0.3)] transition-transform hover:scale-105 active:scale-95 ring-2 ring-white/50"
        aria-label="Open AI job drafting"
      >
        {activeCount > 0
          ? <Loader2 size={24} className="animate-spin text-sky-300" />
          : <CheckCircle2 size={24} className="text-sky-300" />}
        {activeCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white ring-2 ring-white">
            {activeCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <aside className="fixed bottom-4 right-4 z-40 w-[calc(100vw-2rem)] max-w-sm overflow-hidden rounded-xl border border-slate-200 bg-white/95 shadow-[0_24px_80px_rgba(15,23,42,0.2)] backdrop-blur-xl">
      <div className="flex items-center justify-between border-b border-slate-100 bg-[#0b1f3a] px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          {activeCount > 0
            ? <Loader2 size={15} className="animate-spin text-sky-300" />
            : <CheckCircle2 size={15} className="text-sky-300" />}
          <span className="text-sm font-medium">AI job drafting</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-slate-300">
            <span className={`h-1.5 w-1.5 rounded-full ${streamConnected ? 'bg-emerald-400' : 'bg-amber-400'}`} />
            {streamConnected ? 'Live' : 'Syncing'}
          </span>
          <button
            onClick={() => setIsMinimized(true)}
            className="text-slate-400 transition hover:text-white"
            aria-label="Minimize"
          >
            <Minus size={16} />
          </button>
        </div>
      </div>

      <div className="max-h-[26rem] divide-y divide-slate-100 overflow-y-auto">
        {visibleRuns.map(run => {
          const active = isActiveRun(run);
          const publishReady = isPublishReadyRun(run);
          const failed = run.status === 'failed';
          const completed = run.status === 'completed';
          const resolved = completed || publishReady;
          const cancellable = cancellableStatuses.has(run.status) && !publishReady;
          return (
            <div key={run.id} className="p-4">
              <div className="flex items-start gap-3">
                <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                  resolved ? 'bg-emerald-50 text-emerald-700' : failed ? 'bg-rose-50 text-rose-700' : 'bg-sky-50 text-sky-700'
                }`}>
                  {resolved ? <CheckCircle2 size={16} /> : failed ? <AlertCircle size={16} /> : <Loader2 className="animate-spin" size={16} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="truncate text-sm font-medium text-[#0b1f3a]">
                      {run.coreData?.title || run.originalFilename || 'New job draft'}
                    </div>
                    {!active && (
                      <button onClick={() => dismiss(run.id)} className="text-slate-400 transition hover:text-slate-700" aria-label="Dismiss">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{getStageCopy(run)}</div>
                  {(active || publishReady) && (
                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <div className={`h-full rounded-full transition-all duration-700 ${publishReady ? 'bg-emerald-500' : 'bg-sky-500'}`} style={{ width: `${publishReady ? 100 : Math.max(4, Number(run.progress || 0))}%` }} />
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className="text-[11px] font-medium text-slate-400">
                      {publishReady ? '100% - Ready to publish' : active ? `${Number(run.progress || 0)}% complete` : completed ? 'Draft saved' : 'Draft preserved'}
                    </span>
                    <div className="flex items-center gap-3">
                      {failed ? (
                        <>
                          <button onClick={() => handleCancel(run)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 transition hover:text-rose-600">
                            <X size={13} /> Stop
                          </button>
                          <button onClick={() => handleRetry(run)} className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-700 hover:text-rose-900">
                            <RotateCcw size={13} /> Retry
                          </button>
                        </>
                      ) : (
                        <>
                          {cancellable && (
                            <button onClick={() => handleCancel(run)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 transition hover:text-rose-600">
                              <X size={13} /> Cancel
                            </button>
                          )}
                          <button onClick={() => navigate(`/jobs/${run.jobId}`)} className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 hover:text-sky-900">
                            {publishReady ? 'Review and publish' : completed ? 'Review draft' : 'Continue setup'} <ArrowRight size={13} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default JobProcessingCoordinator;
